import qs from 'qs';
import type { FC, ReactElement } from 'react';
import { useState } from 'react';

import { Button, Dialog, Table } from '@zen/DesignSystem';
import { workflowsRoutes } from '@zen/Routes';
import { useNotification } from '@zen/utils/hooks/useNotification';
import { useNavigationHistory } from '@zen/utils/NavigationHistory';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performMutation } from '@zen/utils/performMutation';

import { useDeleteWorkflowRuleMutation } from '../graphql';
import type { WorkflowRuleFieldTableItem, WorkflowRulesListItem } from '../types';
import { useWorkflowRules } from './useWorkflowRules';
import { getColumnsConfiguration } from './workflowRulesTableConfiguration';

interface Props {
  accountIds?: string[];
  canCreateSOP?: boolean;
  hideAccountsColumn?: boolean;
  ruleInputFields: WorkflowRuleFieldTableItem[];
  ruleOutputFields: WorkflowRuleFieldTableItem[];
  workflowRuleDefinitionId: string;
  workflowRuleDefinitionName: string;
}

const WorkflowRulesTable: FC<Props> = ({
  accountIds,
  canCreateSOP = false,
  hideAccountsColumn = false,
  ruleInputFields,
  ruleOutputFields,
  workflowRuleDefinitionId,
  workflowRuleDefinitionName
}) => {
  const { navigate } = useNavigationHistory();
  const { addSuccess, addError } = useNotification();
  const [currentRuleId, setCurrentRuleId] = useState<string>('');

  const { loading, error, nodes, paginationInfo, totalCount, refetch } = useWorkflowRules(workflowRuleDefinitionId, accountIds);

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const [deleteWorkflowRule] = useDeleteWorkflowRuleMutation();

  const handleDeleteRule = (workflowRuleId: string): void => {
    setCurrentRuleId(workflowRuleId);
    setShowConfirmationModal(true);
  };

  const handleEditRule = (workflowRuleId: string): void => {
    navigate(workflowsRoutes.workflowRuleEdit.getUrl(workflowRuleId));
  };

  const handleDeleteWorkflowRule = async (workflowRuleId: string): Promise<IOkOrErrorResult> => {
    return performMutation({
      mutationFn: () =>
        deleteWorkflowRule({
          variables: {
            workflowRuleId
          }
        }),
      onError: () => {
        addError('Error deleting workflow rule.');
      },
      onSuccess: () => {
        addSuccess('Workflow rule successfully deleted.');
        refetch();
      }
    });
  };

  const handleConfirmDeleteRule = (ruleId: string): void => {
    handleDeleteWorkflowRule(ruleId);
    setShowConfirmationModal(false);
  };

  const filteredRuleInputFields = hideAccountsColumn
    ? ruleInputFields.filter((field) => field.name !== 'accountId')
    : ruleInputFields;

  const prepareRuleFields = (ruleFields: WorkflowRuleFieldTableItem[]) => ruleFields.filter((field) => !field.hidden);

  const columns = getColumnsConfiguration({
    ruleInputFields: prepareRuleFields(filteredRuleInputFields),
    ruleOutputFields: prepareRuleFields(ruleOutputFields),
    onDelete: handleDeleteRule,
    onEdit: handleEditRule
  });

  const createRuleButton = (
    <Button
      className="ml-3"
      iconLeft="zicon-add"
      onClick={() =>
        navigate({
          pathname: workflowsRoutes.workflowRuleCreate.getUrl(workflowRuleDefinitionId || ''),
          search: qs.stringify(
            {
              accountIds
            },
            { addQueryPrefix: false, encode: false, arrayFormat: 'comma' }
          )
        })
      }
      variant="ghost"
    >
      Create rule
    </Button>
  );

  const getWorkflowRulesTable = (workflowRulesList: WorkflowRulesListItem[]): ReactElement => (
    <Table<WorkflowRulesListItem>
      actions={canCreateSOP && createRuleButton}
      columns={columns}
      data={workflowRulesList}
      error={!!error}
      loading={loading}
      paginationInfo={paginationInfo}
      tableId="workflowRulesTable"
      title={workflowRuleDefinitionName}
      totalCountConfig={{
        totalCount,
        entityName: 'workflow rule'
      }}
    />
  );

  return (
    <>
      <Dialog
        isOpen={showConfirmationModal}
        message="Do you want to remove this workflow rule?"
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={() => handleConfirmDeleteRule(currentRuleId)}
      />
      {getWorkflowRulesTable(nodes)}
    </>
  );
};

export default WorkflowRulesTable;
